
import { computeVideoRect,VideoDisplayInfo,sourceUvToDisplayUv } from "./video_display_helper";
const MAX_BLOBS = 256;
const FLOATS_PER_INSTANCE = 8;

export type PackedBlobInstance = {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  ndirx: number;
  ndiry: number;
  colorIndex: number;
  confidence: number;
};

export type PackedBlobInstancesResult = {
  packed: Float32Array;
  instanceCount: number;
  floatsPerInstance: number;
};

export type Blob2Like = {
  alive: number;
  x: number;
  y: number;
  width: number;
  height: number;
  ndirx: number;
  ndiry: number;
  orientationconfidence: number;
  circletype: number;
  colorindex: number;
  blueperc: number;
  greenperc: number;
  redperc: number;
  blackperc: number;
  failcode: number;

  colorpair?: {
    vcb0?: { v: number; cb: number; cr: number; xf?: number; yf?: number };
    vcb1?: { v: number; cb: number; cr: number; xf?: number; yf?: number };
  };

  evidence?: {
    blueScore: number;
    greenScore: number;
    redScore: number;
    blackScore: number;
    unknownScore: number;
    totalWeight: number;
    colorWeight: number;
    blueGreenScore: number;
    redVsBlueGreen: number;
  };
};

export const BLOB_INSTANCE_FLOATS = 16;
export const BLOB_INSTANCE_BYTES = BLOB_INSTANCE_FLOATS * 4;

export type BlobInstanceCpu = {
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
  dirX: number;
  dirY: number;
  conf: number;
  glow: number;
  colorA_r: number;
  colorA_g: number;
  colorA_b: number;
  colorA_a: number;
  colorB_r: number;
  colorB_g: number;
  colorB_b: number;
  colorB_a: number;
};

export const blobVertexBuffers: GPUVertexBufferLayout[] = [
  {
    arrayStride: 8,
    stepMode: "vertex",
    attributes: [
      { shaderLocation: 0, offset: 0, format: "float32x2" },
    ],
  },
  {
    arrayStride:32,
    stepMode: "instance",
    attributes: [
    { shaderLocation: 1, offset:  0, format: "float32x2" }, // center
    { shaderLocation: 2, offset:  8, format: "float32x2" }, // radius
    { shaderLocation: 3, offset: 16, format: "float32x2" }, // dir
    { shaderLocation: 4, offset: 24, format: "float32"   }, // colorIndex
    { shaderLocation: 5, offset: 28, format: "float32"   }, // confidence
    
    ],
  },
];

export function createBlobQuadVertexBuffer(device: GPUDevice): GPUBuffer {
  const quad = new Float32Array([
    -1, -1,
     1, -1,
    -1,  1,
     1,  1,
  ]);

  const buffer = device.createBuffer({
    size: quad.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    mappedAtCreation: true,
  });
  new Float32Array(buffer.getMappedRange()).set(quad);
  buffer.unmap();
  return buffer;
}



export function createOrUpdateInstanceBuffer(
  device: GPUDevice,
  existing: GPUBuffer | null,
  instances: Float32Array,
  instanceBytes: number
): GPUBuffer {
  const requiredSize = MAX_BLOBS * instanceBytes;

  const buffer =
    existing ??
    device.createBuffer({
      size: requiredSize,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });

  device.queue.writeBuffer(
    buffer,
    0,
    instances.buffer,
    instances.byteOffset,
    instances.byteLength
  );

  return buffer;
}

export function createBlobUniformBuffer(device: GPUDevice): GPUBuffer {
  return device.createBuffer({
    size: 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
}

export function updateBlobUniforms(
  device: GPUDevice,
  uniformBuffer: GPUBuffer,
  viewportWidth: number,
  viewportHeight: number
) {
  device.queue.writeBuffer(
    uniformBuffer,
    0,
    new Float32Array([viewportWidth, viewportHeight, 0, 0])
  );
}
export function createBlobBindGroupLayout(device: GPUDevice): GPUBindGroupLayout {
  return device.createBindGroupLayout({
    label: "blob-renderer-bgl",
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
        buffer: {
          type: "uniform",
        },
      },
    ],
  });
}

export function createBlobBindGroup(
  device: GPUDevice,
  layout: GPUBindGroupLayout,
  uniformBuffer: GPUBuffer
): GPUBindGroup {
  return device.createBindGroup({
    label: "blob-renderer-bg",
    layout,
    entries: [
      {
        binding: 0,
        resource: {
          buffer: uniformBuffer,
        },
      },
    ],
  });
}

export function createBlobRenderPipeline(
  device: GPUDevice,
  format: GPUTextureFormat,
  shaderModule: GPUShaderModule,
  bindGroupLayout: GPUBindGroupLayout
): GPURenderPipeline {
  return device.createRenderPipeline({
    layout: device.createPipelineLayout({
      bindGroupLayouts: [bindGroupLayout],
    }),
    vertex: {
      module: shaderModule,
      entryPoint: "vs_main",
      buffers: blobVertexBuffers,
    },
    fragment: {
      module: shaderModule,
      entryPoint: "fs_main",
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: "src-alpha",
              dstFactor: "one-minus-src-alpha",
              operation: "add",
            },
            alpha: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add",
            },
          },
          writeMask: GPUColorWrite.ALL,
        },
      ],
    },
    primitive: {
      topology: "triangle-strip",
      stripIndexFormat: undefined,
    },
     depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: false,
        depthCompare: "always", // or "less" if you want real depth testing
  },
  });
}

export function drawBlobInstances(
  pass: GPURenderPassEncoder,
  pipeline: GPURenderPipeline,
  bindGroup: GPUBindGroup,
  quadBuffer: GPUBuffer,
  instanceBuffer: GPUBuffer,
  instanceCount: number
) {
  if (instanceCount <= 0) return;

  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  pass.setVertexBuffer(0, quadBuffer);
  pass.setVertexBuffer(1, instanceBuffer);
  pass.draw(4, instanceCount, 0, 0);
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function remap01(x: number, lo: number, hi: number): number {
  if (hi <= lo) return 0;
  return clamp01((x - lo) / (hi - lo));
}

function finite(n: number): boolean {
  return Number.isFinite(n);
}

function normalize2(x: number, y: number): [number, number] {
  const len = Math.hypot(x, y);
  if (len < 1e-6) return [1, 0];
  return [x / len, y / len];
}

function ycbcrToApproxRgb(v: number, cb: number, cr: number): [number, number, number] {
  const Y = v;
  const Cb = cb - 0.5;
  const Cr = cr - 0.5;
  const r = clamp01(Y + 1.402 * Cr);
  const g = clamp01(Y - 0.344136 * Cb - 0.714136 * Cr);
  const b = clamp01(Y + 1.772 * Cb);
  return [r, g, b];
}

function chooseSemanticColor(blob: Blob2Like): [number, number, number, number] {
  const bp = blob.blueperc || 0;
  const gp = blob.greenperc || 0;
  const rp = blob.redperc || 0;
  const kp = blob.blackperc || 0;

  if (kp > bp && kp > gp && kp > rp) return [0.08, 0.08, 0.09, 0.95];
  if (bp >= gp && bp >= rp) return [0.15, 0.45, 1.0, 0.95];
  if (gp >= rp) return [0.10, 0.85, 0.30, 0.95];
  return [1.0, 0.18, 0.18, 0.95];
}

function chooseHalfColors(blob: Blob2Like, useSampledHalves = true): {
  colorA: [number, number, number, number];
  colorB: [number, number, number, number];
} {
  const v0 = blob.colorpair?.vcb0;
  const v1 = blob.colorpair?.vcb1;

  if (useSampledHalves && v0 && v1) {
    const a = ycbcrToApproxRgb(v0.v, v0.cb, v0.cr);
    const b = ycbcrToApproxRgb(v1.v, v1.cb, v1.cr);
    return {
      colorA: [a[0], a[1], a[2], 0.95],
      colorB: [b[0], b[1], b[2], 0.95],
    };
  }

  const c = chooseSemanticColor(blob);
  return { colorA: c, colorB: c };
}

export function blobLooksRenderable(
  b: Blob2Like,
  frameWidth: number,
  frameHeight: number
): boolean {
  if (!b) return false;
  if (!finite(b.x) || !finite(b.y)) return false;
  if (!finite(b.width) || !finite(b.height)) return false;

  if (b.alive <= 0) return false;
  if (b.width < 4 || b.height < 4) return false;
  if (b.width > frameWidth * 0.5 || b.height > frameHeight * 0.5) return false;

  if (b.x < 0 || b.y < 0 || b.x >= frameWidth || b.y >= frameHeight) return false;

  return true;
}



export function packBlobInstances(
  blobs: Blob2Like[],
  info: VideoDisplayInfo,
) {
  const rect = computeVideoRect(info);
  const packed: PackedBlobInstance[] = [];

  for (const b of blobs) {
    if (!b.alive) continue;

    const srcU = b.x / info.srcW;
    const srcV = b.y / info.srcH;

    const duv = sourceUvToDisplayUv(srcU, srcV, info.mode);

    const cx = rect.x + duv.u * rect.width;
    const cy = rect.y + duv.v * rect.height;

    const rx = 0.5 * b.width * (rect.width / info.srcW);
    const ry = 0.5 * b.height * (rect.height / info.srcH);

    packed.push({
      cx,
      cy,
      rx,
      ry,
      ndirx: b.ndirx,
      ndiry: b.ndiry,
      colorIndex: b.colorindex,
      confidence: b.orientationconfidence,
    });
  }

  const floatsPerInstance = 8;
  const packedArray = new Float32Array(packed.length * floatsPerInstance);

  for (let i = 0; i < packed.length; i++) {
    const p = packed[i];
    const o = i * floatsPerInstance;

    packedArray[o + 0] = p.cx;
    packedArray[o + 1] = p.cy;
    packedArray[o + 2] = p.rx;
    packedArray[o + 3] = p.ry;
    packedArray[o + 4] = p.ndirx;
    packedArray[o + 5] = p.ndiry;
    packedArray[o + 6] = p.colorIndex;
    packedArray[o + 7] = p.confidence;
  }

  return { packed: packedArray, instanceCount: packed.length, rect };
}